import { supabase } from '@/lib/supabase';
import { addDays, isAfter, isBefore, parseISO } from 'date-fns';

export interface DashboardStats {
  upcomingSessions: UpcomingSession[];
  pendingCheckouts: PendingCheckout[];
  newSubscriptions: NewSubscription[];
  subscriptionsExpiringSoon: ExpiringSubscription[];
  expiredSubscriptions: ExpiredSubscription[];
}

export interface UpcomingSession {
  id: string;
  start_date: string;
  end_date: string;
  duration_minutes: number;
  mode: 'online' | 'onsite' | 'hybrid';
  status: string;
  class: {
    id: string;
    display_id: string;
    course: {
      name: string;
      family: {
        name: string;
      };
    };
    subscription?: {
      id: string;
      subscription_customers?: {
        customer: {
          id: string;
          name: string;
          domain?: string;
        };
      }[];
    };
  };
  instructor: {
    first_name: string;
    last_name: string;
  } | null;
}

export interface PendingCheckout {
  id: string;
  start_date: string;
  duration_minutes: number;
  class: {
    id: string;
    display_id: string;
    course: {
      name: string;
    };
  };
  instructor: {
    first_name: string;
    last_name: string;
  } | null;
  org_name?: string;
}

export interface NewSubscription {
  id: string;
  display_id: string;
  start_date: string;
  state: string;
  created_at: string;
  subscription_customers: {
    customer: {
      name: string;
    };
  }[];
  items: {
    subscription_plan: {
      name: string;
    };
    quantity: number;
    unit_price: number;
  }[];
}

export interface ExpiringSubscription {
  id: string;
  display_id: string;
  end_date: string;
  state: string;
  subscription_customers: {
    customer: {
      name: string;
    };
  }[];
  items: {
    subscription_plan: {
      name: string;
    };
    quantity: number;
    unit_price: number;
  }[];
}

export interface ExpiredSubscription {
  id: string;
  display_id: string;
  end_date: string;
  state: string;
  subscription_customers: {
    customer: {
      name: string;
    };
  }[];
  items: {
    subscription_plan: {
      name: string;
    };
    quantity: number;
    unit_price: number;
  }[];
}

export async function fetchUpcomingSessions(limit = 10): Promise<UpcomingSession[]> {
  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('class_sessions')
      .select(`
        id,
        start_date,
        end_date,
        duration_minutes,
        mode,
        status,
        class:classes!inner (
          id,
          display_id,
          course:courses!inner (
            name,
            family:course_families!inner (
              name
            )
          ),
          subscription:subscriptions (
            id,
            subscription_customers (
              customer:customers (
                id,
                name,
                domain
              )
            )
          )
        ),
        instructor:instructors (
          first_name,
          last_name
        )
      `)
      .gte('start_date', now)
      .eq('status', 'confirmed')
      .order('start_date', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching upcoming sessions:', error);
    return [];
  }
}

export async function fetchPendingCheckouts(limit = 10): Promise<PendingCheckout[]> {
  try {
    const now = new Date().toISOString();
    
    // First, get sessions that have ended but don't have done checkouts
    const { data: sessionsData, error: sessionsError } = await supabase
      .from('class_sessions_')
      .select(`
        class_session_id,
        start_date,
        duration_minutes,
        class_id,
        display_id,
        name,
        org_name,
        instructor_name
      `)
      .lt('end_date', now)
      .in('class_session_status', ['expected', 'confirmed'])
      .order('start_date', { ascending: false })
      .limit(50); // Get more to filter

    if (sessionsError) throw sessionsError;

    if (!sessionsData || sessionsData.length === 0) {
      return [];
    }

    // Get session IDs that already have done checkouts
    const sessionIds = sessionsData.map(s => s.class_session_id);
    const { data: checkoutsData, error: checkoutsError } = await supabase
      .from('class_session_checkouts')
      .select('session_id')
      .in('session_id', sessionIds)
      .eq('status', 'done');

    // Filter out sessions that already have checkouts
    const doneSessionIds = new Set(
      (checkoutsData || []).map(c => c.session_id)
    );

    const pendingCheckouts = sessionsData
      .filter(session => !doneSessionIds.has(session.class_session_id))
      .map(session => ({
        id: session.class_session_id,
        start_date: session.start_date,
        duration_minutes: session.duration_minutes,
        class: {
          id: session.class_id,
          display_id: session.display_id,
          course: {
            name: session.name
          }
        },
        instructor: session.instructor_name ? {
          first_name: session.instructor_name.split(' ')[0] || '',
          last_name: session.instructor_name.split(' ').slice(1).join(' ') || ''
        } : null,
        org_name: session.org_name
      }))
      .slice(0, limit);

    return pendingCheckouts;
  } catch (error) {
    console.error('Error fetching pending checkouts:', error);
    return [];
  }
}

export async function fetchNewSubscriptions(days = 7, limit = 10): Promise<NewSubscription[]> {
  try {
    const daysAgo = addDays(new Date(), -days).toISOString();
    
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        id,
        display_id,
        start_date,
        state,
        created_at,
        subscription_customers (
          customer:customers (
            name
          )
        ),
        items:subscription_items (
          subscription_plan:subscription_plans (
            name
          ),
          quantity,
          unit_price
        )
      `)
      .gte('created_at', daysAgo)
      .in('state', ['active', 'trial'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching new subscriptions:', error);
    return [];
  }
}

export async function fetchSubscriptionsExpiringSoon(days = 60, limit = 10): Promise<ExpiringSubscription[]> {
  try {
    const now = new Date();
    const futureDate = addDays(now, days);
    
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        id,
        display_id,
        end_date,

        state,
        subscription_customers (
          customer:customers (
            name
          )
        ),
        items:subscription_items (
          subscription_plan:subscription_plans (
            name
          ),
          quantity,
          unit_price
        )
      `)
      .not('end_date', 'is', null)
      .gte('end_date', now.toISOString())
      .lte('end_date', futureDate.toISOString())
      .in('state', ['active', 'trial'])
      .order('end_date', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching expiring subscriptions:', error);
    return [];
  }
}

export async function fetchExpiredSubscriptions(limit = 10): Promise<ExpiredSubscription[]> {
  try {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        id,
        display_id,
        end_date,
        state,
        subscription_customers (
          customer:customers (
            name
          )
        ),
        items:subscription_items (
          subscription_plan:subscription_plans (
            name
          ),
          quantity,
          unit_price
        )
      `)
      .not('end_date', 'is', null)
      .lt('end_date', now)
      .in('state', ['active', 'trial', 'expired'])
      .order('end_date', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching expired subscriptions:', error);
    return [];
  }
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const [
    upcomingSessions,
    pendingCheckouts,
    newSubscriptions,
    subscriptionsExpiringSoon,
    expiredSubscriptions
  ] = await Promise.all([
    fetchUpcomingSessions(),
    fetchPendingCheckouts(),
    fetchNewSubscriptions(),
    fetchSubscriptionsExpiringSoon(),
    fetchExpiredSubscriptions()
  ]);

  return {
    upcomingSessions,
    pendingCheckouts,
    newSubscriptions,
    subscriptionsExpiringSoon,
    expiredSubscriptions
  };
}
